import { isDiscontinued } from '@/lib/app-categories/discontinued'
import {
  Classification,
  Recommendation,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { App, AppCategoryRecommendation, Category, Deal, Review } from '@/payload-types'
import { queryPayload } from '@/lib/payloadClient'
import { determineDealsToDisplay } from '@/components/content/deals/helpers'
import { hasRichTextContent } from '@/lib/rich-text'
import { isPopulatedObject } from '@/lib/utils'

// Shared predicate: determines if an ACR belongs in the main ranking list.
// Used by orderAppsByRecommendation() and BestContainer's mainListAppIds.
export function isMainListApp(rec: AppCategoryRecommendation): boolean {
  if (isDiscontinued(rec)) return false
  if (rec.classification === Classification.Primary) {
    return true
  }

  if (rec.classification === Classification.Secondary) {
    if (
      rec.recommendation === Recommendation.TopPick ||
      rec.recommendation === Recommendation.Recommended
    ) {
      return true
    }
  }

  return false
}

/**
 * Fetches deal counts for a list of app IDs client-side.
 * This replaces the previous getDealClaimCounts which required deals to be populated in the SSR query.
 */
export async function fetchDealCountsForApps(
  appIds: Array<App['id']>,
): Promise<{ dealCount: number; claimCount: number }> {
  const deduplicatedAppIds = [...new Set(appIds)]

  if (!deduplicatedAppIds.length) return { dealCount: 0, claimCount: 0 }

  const { docs: deals } = await queryPayload({
    collection: 'deal',
    select: {
      claimed: true,
      app: true,
      order: true,
      featuredOrder: true,
      startDate: true,
      endDate: true,
    },
    where: { app: { in: deduplicatedAppIds } },
    depth: 0,
    pagination: false,
  })

  const dealsToDisplay = determineDealsToDisplay(deals as Deal[])

  return {
    dealCount: dealsToDisplay.length,
    claimCount: dealsToDisplay.reduce((acc, deal) => acc + (deal.claimed || 0), 0),
  }
}

/**
 * Deduplicates and orders apps by recommendation type and returns various lists
 */
export function orderAppsByRecommendation(
  appCategoryRecommendations: AppCategoryRecommendation[] = [],
  merge = false,
) {
  const recs = Array.isArray(appCategoryRecommendations)
    ? appCategoryRecommendations.filter(Boolean)
    : []

  const deduplicate = (source: AppCategoryRecommendation[]): AppCategoryRecommendation[] => {
    const seenAppIds = new Set<App['id']>()
    return source.filter((rec) => {
      const app = rec.app
      if (!isPopulatedObject<App>(app) || !app.slug || !app.id || seenAppIds.has(app.id)) {
        return false
      }
      seenAppIds.add(app.id)
      return true
    })
  }
  const activeRecs = recs.filter((rec) => !isDiscontinued(rec))
  const deduplicatedRecs = deduplicate(activeRecs)
  const activeAppIds = new Set(deduplicatedRecs.map((rec) => (rec.app as App).id))
  const discontinuedApps = merge
    ? []
    : deduplicate(
        recs.filter(
          (rec) =>
            isDiscontinued(rec) && isPopulatedObject<App>(rec.app) && !activeAppIds.has(rec.app.id),
        ),
      )
  const discontinuedWithSummary = discontinuedApps.filter((rec) => hasRichTextContent(rec.summary))

  // Filter for main list: primary apps + secondary apps with Top Pick or Recommended
  // Secondary+capable apps (with or without summary) go to "Notable {Category}" section instead
  const mainList = deduplicate(activeRecs.filter(isMainListApp))

  // Secondary apps NOT promoted to main list (only NotRecommended or Capable)
  // Secondary apps with TopPick/Recommended are in mainList
  const secondaryApps = merge
    ? []
    : deduplicatedRecs.filter((rec) => {
        if (rec.classification !== Classification.Secondary) {
          return false
        }

        if (
          rec.recommendation === Recommendation.TopPick ||
          rec.recommendation === Recommendation.Recommended
        ) {
          return false
        }

        return true
      })

  // Secondary+capable apps (with or without summary) — shown in a "Notable {Category}" section
  const notableCategoryApps = merge
    ? []
    : deduplicatedRecs.filter((rec) => {
        return (
          rec.classification === Classification.Secondary &&
          rec.recommendation === Recommendation.Capable
        )
      })
  const sortByOrder = (a: AppCategoryRecommendation, b: AppCategoryRecommendation) => {
    const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
    const bOrder = b.order ?? Number.MAX_SAFE_INTEGER

    return aOrder - bOrder
  }

  // Sort main list by recommendation type
  const sourceList = merge ? deduplicatedRecs : mainList
  const mainListByType: {
    [Recommendation.TopPick]: AppCategoryRecommendation[]
    [Recommendation.Recommended]: AppCategoryRecommendation[]
    [Recommendation.Capable]: AppCategoryRecommendation[]
    [Recommendation.NotRecommended]: AppCategoryRecommendation[]
  } = {
    [Recommendation.TopPick]: sourceList
      .filter((rec) => rec.recommendation === Recommendation.TopPick)
      .sort(sortByOrder),
    [Recommendation.Recommended]: sourceList
      .filter((rec) => rec.recommendation === Recommendation.Recommended)
      .sort(sortByOrder),
    [Recommendation.Capable]: sourceList
      .filter((rec) => rec.recommendation === Recommendation.Capable)
      .sort(sortByOrder),
    [Recommendation.NotRecommended]: sourceList
      .filter((rec) => rec.recommendation === Recommendation.NotRecommended)
      .sort(sortByOrder),
  }

  const orderedAppRecs: AppCategoryRecommendation[] = [
    ...mainListByType[Recommendation.TopPick],
    ...mainListByType[Recommendation.Recommended],
    ...mainListByType[Recommendation.Capable],
    ...mainListByType[Recommendation.NotRecommended],
  ]

  // orderedApps includes apps from orderedAppRecs PLUS secondary+capable apps with summaries
  const secondaryCapableWithSummary = merge
    ? []
    : deduplicatedRecs.filter((rec) => {
        if (rec.classification !== Classification.Secondary) return false
        if (rec.recommendation !== Recommendation.Capable) return false

        const hasSummary = hasRichTextContent(rec.summary)
        if (hasSummary) return true

        const app = rec.app
        if (!isPopulatedObject<App>(app)) return false

        const [reviewDoc] = app.review?.docs || []
        const review = isPopulatedObject<Review>(reviewDoc) ? reviewDoc : undefined
        return hasRichTextContent(review?.alternativeSummary)
      })

  const orderedApps: App[] = [
    ...orderedAppRecs.map((rec) => rec.app as App),
    ...secondaryCapableWithSummary.map((rec) => rec.app as App),
  ]

  const topPickCount = mainListByType[Recommendation.TopPick].length
  const recommendedCount = mainListByType[Recommendation.Recommended].length

  return {
    orderedApps,
    orderedAppRecs,
    secondaryApps,
    secondaryCapableWithSummary,
    notableCategoryApps: [...notableCategoryApps, ...discontinuedApps],
    discontinuedWithSummary,
    topPickCount,
    recommendedCount,
  }
}

export function convertAppToAppCategoryRecommendation(
  app: App,
  categoryId?: Category['id'],
): AppCategoryRecommendation | null {
  if (!categoryId) {
    return null
  }

  const category = app.categoryRecommendations?.docs?.find(
    (rec): rec is AppCategoryRecommendation & { category: Category } => {
      if (!isPopulatedObject<AppCategoryRecommendation>(rec)) return false
      if (!isPopulatedObject<Category>(rec.category)) return false
      return rec.category.id === categoryId
    },
  )

  if (!category) {
    return null
  }

  return {
    id: category.id,
    app,
    recommendation: category.recommendation,
    bestFor: category.bestFor,
    summary: category.summary,
    category: category.category,
    classification: category.classification,
    discontinuedAt: category.discontinuedAt,
    order: category.order,
    updatedAt: category.updatedAt,
    createdAt: category.createdAt,
  }
}
